import type { StationeryTemplate } from '../../../DesignsGallery.types';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { useGetDiscountByVendor } from './useGetDiscountByVendor';
import type { PromotionConfig } from '@apps/card/routes/CardCustomizer/usePromotionConfig';
import { useFormatSearchParam, toFormatList } from '../../Filters/useFormatSearchParam';
import { isDigitalProduct } from '@apps/card/components/ProductUIConfig/isDigitalProduct';
import { CardFormat } from '../../../../ProductDetailsPage/constants';
import { useProductTypes } from '@apps/card/components/ProductUIConfig/useProductTypes';
import { useCardGeoCurrency } from '@apps/card/hooks/useCardGeoCurrency';
import { usePrintPrices } from '@apps/card/hooks/usePrintPrices';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

type TemplatePriceData = {
  name: string;
  productId: string;
  productType: string;
  basePrice: string;
  preDiscountedPrice: string | undefined;
  discount:
    | {
        promoCode: string;
        type: 'percent';
        percentage: number;
        fullPromoConfig: PromotionConfig;
      }
    | undefined;
};

const isNotNull = (input: unknown): input is TemplatePriceData => {
  return !!input;
};

export const useStationeryTemplateProductPrices = (item: Pick<StationeryTemplate, 'products'>) => {
  const products = item.products;
  const priceCalculator = usePrintPrices(products);

  const [format] = useFormatSearchParam();
  const formats = toFormatList(format);

  const productTypes = useProductTypes();
  const formatPrice = useMinorUnitsToPriceString();
  const { getDiscountByVendor } = useGetDiscountByVendor();

  const targetCurrency = useCardGeoCurrency();

  const [isLocalCurrencyEnabled] = useIsFeatureEnabled('printLocalCurrency');

  const basePrices = products
    .filter(product => formats.includes(isDigitalProduct(product) ? CardFormat.premiumDigital : CardFormat.paper))
    .map(p => {
      const productInfo = productTypes.find(prod => prod.type === p.productType);

      const discount = getDiscountByVendor(p.vendor);

      const { unitPriceInMinorUnits, discounts } = priceCalculator.getProductBasePrice(p.id, productInfo?.defaultQuantity, discount);

      const formattedPrice = formatPrice(unitPriceInMinorUnits, { canShowFreeLabel: true, currency: isLocalCurrencyEnabled ? targetCurrency : undefined });

      if (!productInfo) {
        return null;
      }

      const preDiscountedPrice = discounts?.preDiscountedUnitPriceInMinorUnits;
      const formattedPreDiscountedPrice = preDiscountedPrice !== undefined ? formatPrice(preDiscountedPrice, { canShowFreeLabel: true }) : undefined;

      return {
        name: productInfo?.translations.galleryPriceStringName,
        productId: p.id,
        productType: p.productType,
        basePrice: formattedPrice,
        preDiscountedPrice: formattedPreDiscountedPrice,
        discount,
      };
    })
    .filter(isNotNull);

  return basePrices;
};
